import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DeliveryHistory, Address } from '../types/api';
import OrderStatus, { OrderStage } from '../components/order/OrderStatus';
import DeliveryDetails from '../components/order/DeliveryDetails';
import OrderInfo from '../components/order/OrderInfo';
import { subscriptionService } from '../services';
import CompleteDeliveryModal from '../components/order/CompleteDeliveryModal';
import DriverMessageChat from '../components/order/DriverMessageChat';
import { toast } from 'react-hot-toast';
import apiClient from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import driverMessageService from '../services/driverMessageService';
import ReviewModal from '../components/order/ReviewModal';
import reviewService from '../services/reviewService';
import { SiLine } from 'react-icons/si';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TipDriverModal from '../components/order/TipDriverModal';
import { tipService } from '../services/tipService';

// Driver location type
interface DriverLocation {
  lat: number;
  lng: number;
  updatedAt: string;
}

// Delivery tracking interface for confirmation data
interface DeliveryTracking {
  proof_photo_url?: string;
  delivery_notes?: string;
  delivered_at?: string;
}

// Extend DeliveryHistory type with additional properties
interface ExtendedDeliveryHistory extends DeliveryHistory {
  confirmationImages?: string[];
  confirmationNotes?: string;
  tracking?: DeliveryTracking;
}

// Review type
interface DeliveryReview {
  id: string;
  rating: number;
  comment?: string;
  images?: string[];
  createdAt: string;
}

const formatAddress = (address: Address) => {
  const parts = [
    address.street,
    address.city,
    address.state,
    address.zipCode,
    address.country || 'Thailand'
  ].filter(Boolean);

  // Split into two lines
  const midPoint = Math.ceil(parts.length / 2);
  const line1 = parts.slice(0, midPoint).join(', ');
  const line2 = parts.slice(midPoint).join(', ');

  return { line1, line2 };
};

export default function DeliveryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState<ExtendedDeliveryHistory | null>(null);
  const [formattedAddress, setFormattedAddress] = useState<{ line1: string; line2: string } | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>();
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [driverLocation, setDriverLocation] = useState<DriverLocation | null>(null);
  const [driverId, setDriverId] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [customerLocation, setCustomerLocation] = useState<{lat: number, lng: number} | null>(null);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [hasReviewed, setHasReviewed] = useState<boolean>(false);
  const [reviewLoading, setReviewLoading] = useState<boolean>(false);
  const [reviewData, setReviewData] = useState<DeliveryReview | null>(null);
  const [showDeliveryResultsModal, setShowDeliveryResultsModal] = useState(false);
  const [deliveryTracking, setDeliveryTracking] = useState<DeliveryTracking | null>(null);
  const [showTipModal, setShowTipModal] = useState(false);
  const [hasUserTipped, setHasUserTipped] = useState(false);
  const [estimatedArrivalTime, setEstimatedArrivalTime] = useState<Date | null>(null);
  const [isCalculatingEta, setIsCalculatingEta] = useState(false);
  const [routeDuration, setRouteDuration] = useState<number | null>(null); // in minutes
  
  // Reference for the map
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const customerMarkerRef = useRef<any>(null);
  const directionsRendererRef = useRef<any>(null);

  const { t } = useTranslation();

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) return;
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Fetch delivery data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch delivery data first
        if (id) {
          const deliveryResponse = await apiClient.get(`/deliveries/${id}`);
          if (deliveryResponse.data && deliveryResponse.data.data) {
            setDelivery(deliveryResponse.data.data);
            
            // Get subscription details from the delivery response
            const deliveryData = deliveryResponse.data.data;
            
            if (deliveryData.subscription) {
              setSubscriptionId(deliveryData.subscription.id);
              setSubscriptionDetails(deliveryData.subscription);
            }
            
            // Set formatted address from delivery response
            if (deliveryData.address) {
              setFormattedAddress(formatAddress(deliveryData.address));
              
              // Check if we have coordinates directly in the coordinates property
              if (deliveryData.address.coordinates) {
                setCustomerLocation({
                  lat: parseFloat(deliveryData.address.coordinates.lat),
                  lng: parseFloat(deliveryData.address.coordinates.lng)
                });
              } 
              // Fallback to latitude/longitude if present
              else if (deliveryData.address.latitude && deliveryData.address.longitude) {
                setCustomerLocation({
                  lat: parseFloat(deliveryData.address.latitude),
                  lng: parseFloat(deliveryData.address.longitude)
                });
              }
            }

            // Get driver ID if available
            if (deliveryData.driver_id) {
              setDriverId(deliveryData.driver_id);
            }
            
            // Fetch delivery tracking data for confirmation details
            if (deliveryData.stage === 'arrived') {
              fetchDeliveryTracking(id);
              // Check if user has already tipped
              checkUserTipped(id);
            }
          }
        }
      } catch (error) {
        toast.error('Failed to load delivery details');
      }
    };

    fetchData();
  }, [id]);
  
  // Function to fetch delivery tracking data
  const fetchDeliveryTracking = async (deliveryId: string) => {
    try {
      const trackingResponse = await apiClient.get(`/deliveries/${deliveryId}/tracking`);
      if (trackingResponse.data && trackingResponse.data.data) {
        setDeliveryTracking(trackingResponse.data.data);
        
        // Update the delivery object with confirmation data
        setDelivery(prev => {
          if (!prev) return null;
          
          const trackingData = trackingResponse.data.data;
          return {
            ...prev,
            confirmationImages: trackingData.proof_photo_url ? 
              [trackingData.proof_photo_url].filter(Boolean) : [],
            confirmationNotes: trackingData.delivery_notes || '',
            tracking: trackingData
          };
        });
      }
    } catch (error) {
      // Error handling without logging
    }
  };

  // Function to check if user has already tipped for this delivery
  const checkUserTipped = async (deliveryId: string) => {
    try {
      const tipped = await tipService.hasUserTipped(deliveryId);
      setHasUserTipped(tipped);
    } catch (error) {
      console.error('Error checking tip status:', error);
    }
  };

  // Fetch driver location periodically
  useEffect(() => {
    if (!driverId) return;

    const fetchDriverLocation = async () => {
      try {
        const response = await apiClient.get(`/drivers/${driverId}/location`);
        if (response.data && response.data.data) {
          const locationData = {
            lat: response.data.data.current_location.lat,
            lng: response.data.data.current_location.lng,
            updatedAt: response.data.data.updated_at
          };
          setDriverLocation(locationData);
        }
      } catch (error) {
        // Error handling without logging
      }
    };

    // Fetch immediately
    fetchDriverLocation();

    // Then set up polling every 30 seconds
    const interval = setInterval(fetchDriverLocation, 30000);
    
    return () => clearInterval(interval);
  }, [driverId]);

  // Initialize map
  useEffect(() => {
    if (!window.google || !window.google.maps || !mapRef.current || !driverLocation) {
      return;
    }
    
    // Create map instance with customer location as center if available, otherwise use driver location
    if (!mapInstanceRef.current) {
      const mapOptions = {
        zoom: 14,
        center: customerLocation ? 
          { lat: customerLocation.lat, lng: customerLocation.lng } : // Center on customer address
          { lat: driverLocation.lat, lng: driverLocation.lng }, // Fallback to driver location if no customer address
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      };
      
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
    }

    // Add driver marker
    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: { lat: driverLocation.lat, lng: driverLocation.lng },
        map: mapInstanceRef.current,
        icon: {
          // Use a directly embedded emoji character as fallback if image isn't loading
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#F59E0B', // Amber color
          fillOpacity: 1,
          strokeWeight: 1,
          strokeColor: '#000',
          labelOrigin: new window.google.maps.Point(0, 0)
        },
        label: {
          text: '🛺',
          fontSize: '18px',
          fontWeight: 'bold'
        },
        title: 'Driver Location'
      });
      
      // Create a simple infowindow to show when the marker is clicked
      const infoWindow = new window.google.maps.InfoWindow({
        content: `<div style="font-weight: bold;">Driver location</div>
                 <div>Updated: ${new Date(driverLocation.updatedAt).toLocaleTimeString()}</div>`
      });
      
      markerRef.current.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current, markerRef.current);
      });
    } else {
      markerRef.current.setPosition({ lat: driverLocation.lat, lng: driverLocation.lng });
    }

    // If we have customer location, add marker and route
    if (customerLocation) {
      // Add customer marker if not already created
      if (!customerMarkerRef.current) {
        customerMarkerRef.current = new window.google.maps.Marker({
          position: { lat: customerLocation.lat, lng: customerLocation.lng },
          map: mapInstanceRef.current,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            scaledSize: new window.google.maps.Size(40, 40),
          },
          title: 'Your Location'
        });
      }

      // Create directions service and renderer if not already created
      if (!directionsRendererRef.current) {
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
          suppressMarkers: true, // Don't show default markers
          polylineOptions: {
            strokeColor: '#F59E0B', // Amber color for the route
            strokeWeight: 5,
            strokeOpacity: 0.7
          }
        });
        directionsRendererRef.current.setMap(mapInstanceRef.current);
      }

      // Calculate route from driver to customer
      const directionsService = new window.google.maps.DirectionsService();
      
      directionsService.route(
        {
          origin: { lat: driverLocation.lat, lng: driverLocation.lng },
          destination: { lat: customerLocation.lat, lng: customerLocation.lng },
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result: any, status: string) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            directionsRendererRef.current.setDirections(result);
          }
        }
      );
      
      // Always fit both points on the map, regardless of route calculation
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: driverLocation.lat, lng: driverLocation.lng });
      bounds.extend({ lat: customerLocation.lat, lng: customerLocation.lng });
      
      // Add some padding to ensure both markers are comfortably visible
      mapInstanceRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
    }
  }, [driverLocation, customerLocation]);

  // Fetch unread messages count
  useEffect(() => {
    if (!id || !driverId || showChat) return;
    
    const fetchUnreadMessages = async () => {
      try {
        const count = await driverMessageService.getUnreadCount(id);
        setUnreadMessages(count);
      } catch (error) {
        // Error handling without logging
      }
    };
    
    // Fetch immediately
    fetchUnreadMessages();
    
    // Set up polling every 30 seconds
    const interval = setInterval(fetchUnreadMessages, 30000);
    
    return () => clearInterval(interval);
  }, [id, driverId, showChat]);

  // Check if the delivery has been reviewed
  useEffect(() => {
    if (id && delivery?.stage === 'arrived') {
      const checkReviewStatus = async () => {
        try {
          setReviewLoading(true);
          const reviewed = await reviewService.hasReviewed(id);
          setHasReviewed(reviewed);
          
          // If delivery has been reviewed, fetch the review data
          if (reviewed) {
            const reviewDetails = await reviewService.getReviewByDeliveryId(id);
            if (reviewDetails) {
              setReviewData(reviewDetails);
            }
          }
        } catch (error) {
          // Error handling without logging
        } finally {
          setReviewLoading(false);
        }
      };
      
      checkReviewStatus();
    }
  }, [id, delivery?.stage]);

  // Calculate ETA when driver location or customer location changes
  useEffect(() => {
    if (driverLocation && customerLocation && delivery?.stage && delivery.stage !== 'arrived') {
      calculateETA();
    }
  }, [driverLocation, customerLocation, delivery?.stage]);

  // Periodically recalculate ETA every 2 minutes for ongoing deliveries
  useEffect(() => {
    if (driverLocation && customerLocation && delivery?.stage && delivery.stage !== 'arrived') {
      const interval = setInterval(() => {
        calculateETA();
      }, 120000); // 2 minutes
      
      return () => clearInterval(interval);
    }
  }, [driverLocation, customerLocation, delivery?.stage]);

  const handleStageChange = (newStage: OrderStage) => {
    if (delivery) {
      setDelivery({ ...delivery, stage: newStage });
      
      // Fetch delivery tracking data if the stage changes to 'arrived'
      if (newStage === 'arrived' && id) {
        fetchDeliveryTracking(id);
      }
    }
  };

  const handleInstructionsUpdate = (data: { instructions: string; images: string[] }) => {
    if (delivery) {
      setDelivery({
        ...delivery,
        instructions: data.instructions,
        images: data.images,
        address: {
          ...delivery.address,
          deliveryNote: data.instructions,
          images: data.images
        }
      });
    }
  };

  const handleCompleteDelivery = () => {
    if (delivery) {
      setDelivery({ 
        ...delivery, 
        stage: 'arrived',
        arrivedTime: new Date().toISOString()
      });
      setShowCompleteModal(false);
      
      // Fetch delivery tracking data when delivery is completed
      if (id) {
        fetchDeliveryTracking(id);
      }
    }
  };

  const toggleChat = () => {
    setShowChat(!showChat);
    if (!showChat) {
      // Reset unread count when opening chat
      setUnreadMessages(0);
    }
  };

  const handleOpenReview = () => {
    setShowReviewModal(true);
  };

  const handleViewDeliveryResults = () => {
    setShowDeliveryResultsModal(true);
  };

  // Function to calculate ETA using Google Maps Directions API
  const calculateETA = async () => {
    if (!driverLocation || !customerLocation || !window.google || !window.google.maps) {
      return;
    }

    setIsCalculatingEta(true);

    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      const result = await new Promise<any>((resolve, reject) => {
        directionsService.route(
          {
            origin: { lat: driverLocation.lat, lng: driverLocation.lng },
            destination: { lat: customerLocation.lat, lng: customerLocation.lng },
            travelMode: window.google.maps.TravelMode.DRIVING,
            avoidTolls: false,
            avoidHighways: false
          },
          (result: any, status: string) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              resolve(result);
            } else {
              reject(new Error(`Directions request failed: ${status}`));
            }
          }
        );
      });

      if (result && result.routes && result.routes.length > 0) {
        const route = result.routes[0];
        const leg = route.legs[0];
        const durationInSeconds = leg.duration.value;
        const durationInMinutes = Math.ceil(durationInSeconds / 60);
        
        setRouteDuration(durationInMinutes);
        
        // Calculate estimated arrival time
        const now = new Date();
        const estimatedArrival = new Date(now.getTime() + durationInSeconds * 1000);
        setEstimatedArrivalTime(estimatedArrival);
      }
    } catch (error) {
      console.error('Error calculating ETA:', error);
      setRouteDuration(null);
      setEstimatedArrivalTime(null);
    } finally {
      setIsCalculatingEta(false);
    }
  };

  // Format ETA for display
  const getDisplayEstimatedTime = () => {
    if (!delivery) return '9:00 AM';
    
    if (delivery.stage === 'scheduled') {
      return delivery.estimatedTime || '9:00 AM';
    }
    
    if (estimatedArrivalTime && routeDuration !== null) {
      if (routeDuration < 1) {
        return t('deliveryPage.lessThanMinute');
      }
      return `${routeDuration} ${t('deliveryPage.minutes')}`;
    }
    
    if (isCalculatingEta) {
      return t('deliveryPage.calculatingEta');
    }
    
    // Show "No estimated time" when we don't have location data or calculation failed
    if (!driverLocation || !customerLocation) {
      return t('deliveryPage.etaNotAvailable');
    }
    
    return delivery.estimatedTime || t('deliveryPage.etaNotAvailable');
  };

  if (!delivery) return null;

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-50 space-y-1 pb-6">
        {/* Using the reusable PageHeader component */}
        <PageHeader title="Delivery Details" />
        
        {/* Order status section */}
        <div className="flex flex-col px-4 pb-4 bg-white">
          {delivery.stage !== 'arrived' && (
            <OrderStatus 
              stage={delivery.stage || 'scheduled'}
              estimatedTime={getDisplayEstimatedTime()}
              date={delivery.date}
              arrivedTime={delivery.arrivedTime}
            />
          )}
        </div>
        
        {/* Driver location map */}
        <div className="relative">
          {driverId && delivery.stage !== 'arrived' ? (
            <div 
              ref={mapRef} 
              className="w-full h-[375px] relative z-10"
              style={{ 
                background: '#f3f4f6', 
              }}
            >
              {!driverLocation && 
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mb-2"></div>
                  <div className="text-gray-500">
                    {t('deliveryPage.loadingDriverLocation')}
                  </div>
                </div>
              }
              {driverLocation && !window.google && 
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-20">
                  <div className="text-gray-500">
                    {t('deliveryPage.loadingMap')}
                  </div>
                </div>
              }
              {driverLocation && 
                <div className="absolute bottom-4 left-4 bg-white py-2 px-3 rounded-lg shadow-md z-30 text-sm">
                  <div className="flex items-center">
                    <span className="text-amber-600 mr-2">🛺</span>
                    <div>
                      <p className="font-medium">{t('deliveryPage.driverOnTheWay')}</p>
                      {/* ETA Display */}
                      {isCalculatingEta ? (
                        <p className="text-xs text-blue-600">
                          {t('deliveryPage.calculatingEta')}
                        </p>
                      ) : routeDuration !== null ? (
                        <p className="text-xs text-green-600 font-medium">
                          {routeDuration < 1 
                            ? t('deliveryPage.lessThanMinute')
                            : `${t('deliveryPage.arrivingIn')} ${routeDuration} ${t('deliveryPage.minutes')}`
                          }
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">
                          {t('deliveryPage.etaNotAvailable')}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {t('deliveryPage.lastUpdated')}: {new Date(driverLocation.updatedAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>
          ) : delivery.stage === 'arrived' ? (
            <div className="p-4 bg-white text-center text-gray-600">
              <div className="flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">{t('deliveryPage.deliveryCompleted')}</span>
              </div>
              {delivery.arrivedTime && (
                <p className="text-sm text-gray-500">
                  {t('deliveryPage.deliveredAt')}: {new Date(delivery.arrivedTime).toLocaleTimeString()} {t('deliveryPage.on')} {new Date(delivery.arrivedTime).toLocaleDateString()}
                </p>
              )}
              
              {/* View Delivery Results Button - Only show if there are confirmation images */}
              {delivery?.confirmationImages && delivery.confirmationImages.length > 0 && (
                <button
                  onClick={handleViewDeliveryResults}
                  className="mt-3 px-4 py-2 bg-amber-100 text-amber-800 rounded-md text-sm font-medium"
                >
                  {t('deliveryPage.viewDeliveryPhotos')}
                </button>
              )}
              
              {/* Display Review if exists */}
              {!reviewLoading && reviewData && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <span className="text-sm font-medium mr-2">{t('deliveryPage.yourRating')}:</span>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={16}
                            className={`${
                              star <= reviewData.rating ? 'fill-amber-500 stroke-amber-500' : 'fill-none stroke-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={handleOpenReview}
                      className="text-xs text-blue-600"
                    >
                      {t('common.edit')}
                    </button>
                  </div>
                  
                  {reviewData.comment && (
                    <p className="text-sm text-gray-600 mt-2">{reviewData.comment}</p>
                  )}
                  
                  {reviewData.images && reviewData.images.length > 0 && (
                    <div className="mt-2 flex space-x-2 overflow-x-auto">
                      {reviewData.images.map((img: string, index: number) => (
                        <img 
                          key={index} 
                          src={img} 
                          alt={`${t('deliveryPage.review')} ${index+1}`} 
                          className="h-16 w-16 object-cover rounded-md" 
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-white text-center text-gray-500 text-sm">
              {t('deliveryPage.noDriverAssigned')}
            </div>
          )}
        </div>

        {/* Chat modal */}
        {showChat && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md h-[80vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-semibold">{t('deliveryPage.messageYourDriver')}</h2>
                <button 
                  onClick={toggleChat}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                {id && (
                  <DriverMessageChat 
                    deliveryId={id} 
                    driverId={driverId}
                    driverName={delivery.driver?.name}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <DeliveryDetails 
          address={delivery.address}
          instructions={delivery.instructions || delivery.address?.deliveryNote || ''}
          images={delivery.images?.length ? delivery.images : (delivery.address?.images || [])}
          formattedAddress={formattedAddress}
          subscriptionId={subscriptionId}
          onInstructionsUpdate={handleInstructionsUpdate}
        />

        <OrderInfo 
          id={delivery.id}
          paymentMethod={delivery.paymentMethod}
          paidAt={delivery.paidAt}
          onCopyOrderId={() => {}}
        />

        {/* Contact Buttons */}
        <div className="p-4 bg-white space-y-3 mt-4">
          {/* Review Button - Only show for completed deliveries */}
          {delivery.stage === 'arrived' && !reviewLoading && !hasReviewed && (
            <button 
              onClick={handleOpenReview}
              className="w-full py-3 rounded-lg font-medium flex items-center justify-center bg-blue-500 text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              {t('deliveryPage.rateYourDelivery')}
            </button>
          )}
          {delivery.stage === 'arrived' && reviewLoading && (
            <div className="w-full py-3 bg-gray-200 text-gray-500 rounded-lg font-medium flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500 mr-2"></div>
              {t('common.loading')}
            </div>
          )}
          
          {/* Tip Driver Button - Only show for completed deliveries with driver and if not already tipped */}
          {delivery.stage === 'arrived' && driverId && !hasUserTipped && delivery.driver?.promptpayAccount && (
            <button 
              onClick={() => setShowTipModal(true)}
              className="w-full py-3 rounded-lg font-medium flex items-center justify-center bg-green-500 text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              {t('deliveryPage.tipDriver')}
            </button>
          )}
          
          {/* Layout for Message Driver and Contact Support */}
          {delivery.stage === 'arrived' ? (
            <div className="flex gap-3">
              {driverId && (
                <button 
                  onClick={toggleChat}
                  className="w-1/2 py-3 bg-amber-500 text-white rounded-lg font-medium flex items-center justify-center relative"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  {t('common.message')}
                  {unreadMessages > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                      {unreadMessages}
                    </span>
                  )}
                </button>
              )}
              
              <a 
                href="https://line.me/ti/p/@piika" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-1/2 py-3 bg-[#00B900] text-white rounded-lg font-medium flex items-center justify-center"
              >
                <SiLine className="h-5 w-5 mr-2" />
                {t('common.support')}
              </a>
            </div>
          ) : (
            <div className="space-y-3">
              {driverId && (
                <button 
                  onClick={toggleChat}
                  className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium flex items-center justify-center relative"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  {t('deliveryPage.messageDriver')}
                  {unreadMessages > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                      {unreadMessages}
                    </span>
                  )}
                </button>
              )}
              
              <a 
                href="https://line.me/ti/p/@piika" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full py-3 bg-[#00B900] text-white rounded-lg font-medium flex items-center justify-center"
              >
                <SiLine className="h-5 w-5 mr-2" />
                {t('deliveryPage.contactCustomerSupport')}
              </a>
            </div>
          )}
        </div>
      </div>

      <CompleteDeliveryModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        onComplete={handleCompleteDelivery}
      />

      {/* Review Modal */}
      {id && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            // Refresh review status after modal closes
            if (id) {
              reviewService.hasReviewed(id).then(async (reviewed) => {
                setHasReviewed(reviewed);
                if (reviewed) {
                  const reviewDetails = await reviewService.getReviewByDeliveryId(id);
                  if (reviewDetails) {
                    setReviewData(reviewDetails);
                  }
                }
              });
            }
          }}
          deliveryId={id}
        />
      )}

      {/* Delivery Results Modal */}
      {showDeliveryResultsModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">{t('deliveryPage.deliveryConfirmation')}</h2>
              <button 
                onClick={() => setShowDeliveryResultsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-4">
                {/* Delivery Time */}
                <div>
                  <h3 className="font-medium mb-2">{t('deliveryPage.deliveryTime')}</h3>
                  <p className="text-gray-700 text-sm">
                    {delivery?.tracking?.delivered_at ? 
                      new Date(delivery.tracking.delivered_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 
                      delivery?.arrivedTime ? 
                        new Date(delivery.arrivedTime).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 
                        t('deliveryPage.deliveryTimeNotAvailable')
                    }
                  </p>
                </div>

                <div>
                  <h3 className="font-medium mb-2">{t('deliveryPage.deliveryPhotos')}</h3>
                  {delivery?.confirmationImages && delivery.confirmationImages.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {delivery.confirmationImages.map((img: string, index: number) => (
                        <img 
                          key={index} 
                          src={img} 
                          alt={`${t('deliveryPage.confirmation')} ${index+1}`} 
                          className="w-full aspect-square object-cover rounded-lg" 
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">{t('deliveryPage.noConfirmationPhotos')}</p>
                  )}
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">{t('deliveryPage.deliveryNotes')}</h3>
                  <p className="text-gray-700 text-sm">
                    {delivery?.confirmationNotes || t('deliveryPage.noDeliveryNotes')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tip Driver Modal */}
      {id && driverId && (
        <TipDriverModal
          isOpen={showTipModal}
          onClose={() => {
            setShowTipModal(false);
            // Refresh tip status after modal closes
            if (id) {
              checkUserTipped(id);
            }
          }}
          deliveryId={id}
          driverId={driverId}
          driverName={delivery.driver?.name}
                        driverPromptPay={delivery.driver?.promptpayAccount}
        />
      )}
    </div>
  );
}
